//src/app/features/questionnaires/edit/TaxQuestionnaire/TaxReturnEstimate/index.tsx
import {
  PrepareQuestionnaireState,
  TaxRequestType,
} from 'app/features/questionnaires/create/PrepareQuestionnaire/slice';

type PQ = PrepareQuestionnaireState;
type Level = 0 | 1 | 2 | 3 | 4;
type Addon = { name: string; value: number };

type Estimate = {
  level: Level;
  addons?: Addon[];
  name: string;
};
type Qualifier = (pq: PQ) => boolean;
type Calucalator = (pq: PQ) => Estimate;
type Rule = {
  qualifier: Qualifier;
  calculator: Calucalator;
};

const INDIVIDUAL_PRICE: { [key in Level]: number } = {
  0: 199.0,
  1: 299.0,
  2: 399.0,
  3: 499.0,
  4: 999.0,
};

const BUSINESS_PRICE: { [key in Level]: number } = {
  0: 299.0,
  1: 599.0,
  2: 799.0,
  3: 1299.0,
  4: 1299.0,
};
function getMatchingLevel(
  levels: { [key in Level]?: (value: number) => boolean },
  value: number | null,
): Level {
  if (!value) return 0;
  const _levels = Object.keys(levels) as unknown as Level[];
  return (
    _levels.find((level) => {
      const matcher = levels[level];
      return matcher ? matcher(value) : false;
    }) || 0
  );
}

const FormW2Rule: Rule = {
  qualifier: (pq: PQ) => pq.paidByEmployer === 'yes',
  calculator: (pq: PQ) => ({
    name: 'Form W2',

    level: getMatchingLevel(
      {
        0: (c) => c <= 3,
        1: (c) => c <= 3,
        2: (c) => c > 3 && c <= 5,
        3: (c) => c > 5 && c <= 7,
        4: (c) => c > 7,
      },
      pq.howManyW2SDoYouHave,
    ),
  }),
};

const ScheduleARule: Rule = {
  qualifier: (pq: PQ) => pq.doYouOwnAHome === 'yes',
  calculator: (pq: PQ) => ({
    level: 1,
    name: 'Schedule A',
  }),
};

const ScheduleBRule: Rule = {
  qualifier: (pq: PQ) => pq.investments === 'yes',
  calculator: (pq: PQ) => ({
    name: 'Schedule B',
    level: getMatchingLevel(
      {
        0: (c) => c <= 1,
        1: (c) => c > 1 && c <= 3,
        2: (c) => c > 3 && c <= 5,
        3: (c) => c > 5 && c <= 10,
        4: (c) => c > 10,
      },
      pq.howManyBrokerageAccountsOrPlatformsDidYouUse_Investments,
    ),
  }),
};

const ScheduleCRule: Rule = {
  qualifier: (pq: PQ) => pq.selfEmployed === 'yes',
  calculator: (pq: PQ) => ({
    name: 'Schedule C',
    level: getMatchingLevel(
      {
        0: (c) => c === 0,
        1: (c) => c <= 1,
        2: (c) => c <= 1,
        3: (c) => c > 1 && c <= 2,
        4: (c) => c > 2,
      },
      pq.howManyBusinessesDoYouHaveWhereYouAreTheOnlyMember,
    ),
  }),
};

const ScheduleDRule: Rule = {
  qualifier: (pq: PQ) => pq.didYouEngageInCryptocurrency === 'yes',
  calculator: (pq: PQ) => ({
    name: 'Schedule D',

    level: getMatchingLevel(
      {
        0: (c) => c <= 1,
        1: (c) => c > 1 && c <= 3,
        2: (c) => c > 3 && c <= 5,
        3: (c) => c > 5 && c <= 10,
        4: (c) => c > 10,
      },
      pq.howManyBrokerageAccountsPlatformsDidYouUse_Cryptocurrency,
    ),
  }),
};

const ScheduleERule: Rule = {
  qualifier: (pq: PQ) => pq.rentProperty === 'yes',
  calculator: (pq: PQ) => ({
    name: 'Schedule E',

    level: getMatchingLevel(
      {
        2: (c) => c <= 1,
        3: (c) => c > 1 && c <= 2,
        4: (c) => c > 2,
      },
      pq.howManyRentalPropertiesDidYouHave,
    ),
  }),
};

const ScheduleFRule: Rule = {
  qualifier: (pq: PQ) => pq.doYouHaveAFarm === 'yes',
  calculator: (pq: PQ) => ({
    name: 'Schedule F',

    level: 3,
  }),
};

const K1sRule: Rule = {
  qualifier: (pq: PQ) =>
    pq.doYouHaveBusinessInterestInAPartnershipOrCorporationReceiveAK1Forms ===
    'yes',
  calculator: (pq: PQ) => ({
    name: 'K1s',

    level: getMatchingLevel(
      {
        2: (c) => c <= 2,
        3: (c) => c > 2 && c <= 4,
        4: (c) => c > 4,
      },
      pq.howManyDifferentBusinessesDoYouOwn,
    ),
  }),
};

const WorkInMoreThanOneStateRule: Rule = {
  qualifier: (pq: PQ) => pq.didYouLiveOrWorkInMoreThanOneState === 'yes',
  calculator: (pq: PQ) => ({
    name: 'Work In More Than One State',

    level: 1,
    addons: pq.howManyStateDidYouLiveOrWorkIn
      ? Array.from({ length: pq.howManyStateDidYouLiveOrWorkIn }).map(
          (_, index) => ({
            name: 'How Many State Did You Live Or Work In',
            value: 49.99,
          }),
        )
      : undefined,
  }),
};

const individualRules: Rule[] = [
  FormW2Rule,
  ScheduleARule,
  ScheduleBRule,
  ScheduleCRule,
  ScheduleDRule,
  ScheduleERule,
  ScheduleFRule,
  K1sRule,
  WorkInMoreThanOneStateRule,
];

const businessRules: Rule[] = [
  {
    qualifier: (pq) =>
      pq.isAoaSubscriber === 'yes' &&
      pq.doesAccountantsOnAirDoYourBookKeepingServices === 'no' &&
      pq.doYouHaveAFinancialStatement === 'no',
    calculator: () => ({ level: 3, name: 'No Financial Statement' }),
  },

  {
    qualifier: (pq) =>
      pq.doYouHaveAnyGrossIncomeForThisYearInQuestion === 'yes',
    calculator: (pq) => ({
      level: pq.above10kGrossIncome === 'yes' ? 3 : 2,
      name: 'Above 10k Gross Income',
    }),
  },

  {
    qualifier: (pq) => pq.didYouHaveAnyExpensesForThisYearInQuestion === 'yes',
    calculator: (pq) => ({
      level: pq.above10kExpenses === 'yes' ? 3 : 2,
      name: 'Above 10k Expenses',
    }),
  },
];

const getEstimates = (pq: PQ, rules: Rule[]): Estimate[] =>
  rules.filter((r) => r.qualifier(pq)).map((r) => r.calculator(pq));

const getMaximumEstimate = (estimates: Estimate[]): Level =>
  estimates.reduce(
    (max, estimate) => (estimate.level > max ? estimate.level : max),
    0 as Level,
  );

const getAddonsTotalPrice = (estimates: Estimate[]) =>
  estimates
    .filter((e) => e.addons)
    .map((e) => e.addons!)
    .flat()
    .map((addon) => addon.value)
    .reduce((sum, addon) => sum + addon, 0);

const getRulesAndPrices = (
  serviceType: TaxRequestType,
): [Rule[], { [key in Level]: number }] => {
  return serviceType === TaxRequestType.Individual
    ? [individualRules, INDIVIDUAL_PRICE]
    : [businessRules, BUSINESS_PRICE];
};

export const getTaxReturnEstimate = (prepareQuestionnaire?: PQ) => {
  if (!prepareQuestionnaire || !prepareQuestionnaire.taxRequestType)
    return { price: 0, estimates: [] };

  const [rules, PRICES] = getRulesAndPrices(
    prepareQuestionnaire.taxRequestType,
  );

  const estimates = getEstimates(prepareQuestionnaire, rules);

  return {
    price:
      PRICES[getMaximumEstimate(estimates)] + getAddonsTotalPrice(estimates),
    estimates,
  };
};
